import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography, Button, Stack, Chip,
    IconButton, Tooltip, LinearProgress, Alert,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import EditIcon from '@mui/icons-material/Edit';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { mergeDataGridStyles } from '../../styles/dataGridStyles';
import AddParticipantModal from './AddParticipantModal';
import ParticipantResultModal from './ParticipantResultModal';
import SlideShowModal from './SlideShowModal';
import TempPhoneModal from './TempPhoneModal';
import {
    getLocalSessionByUuid, getLocalCourses, sendSession, resetParticipant,
    closeSession, removeParticipant, refreshSessionFromServer, markPassed, processOutbox,
    updateParticipantPhone, fetchParticipantPhoneHistory,
} from '../../services/trainingSessionService';

export default function TrainingSessionDetail() {
    const { uuid } = useParams();
    const navigate = useNavigate();

    const [session, setSession] = useState(null);
    const [course, setCourse] = useState(null);   // курс из IndexedDB — слайды презентации для нативного показа
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [addSource, setAddSource] = useState('people');  // 'people' | 'roster'
    const [addKey, setAddKey] = useState(0);  // bump чтобы пересоздавать модалку и сбрасывать её state
    const [phoneEditFor, setPhoneEditFor] = useState(null);  // участник, которому меняем номер
    const [phoneHistory, setPhoneHistory] = useState([]);    // кто/с чего на что/когда менял номер
    const [previewOpen, setPreviewOpen] = useState(false);
    const mutatingRef = useRef(false);          // true пока идёт мутация — polling в этот момент пропускается
    const lastMutationAtRef = useRef(0);        // момент когда мутация закончилась — polling спит 3s после
    const [resultOpen, setResultOpen] = useState(false);
    const [resultParticipant, setResultParticipant] = useState(null);
    // Нет сети → нельзя слать SMS-ссылки; вместо этого тренер отмечает «прошёл» вручную.
    const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' && navigator.onLine === false);
    useEffect(() => {
        const goOnline = () => setIsOffline(false);
        const goOffline = () => setIsOffline(true);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
    }, []);

    // Чтение — ТОЛЬКО из IndexedDB. После каждой mutation сервис обновляет IDB,
    // мы перечитываем отсюда. Никаких GET при заходе на страницу.
    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getLocalSessionByUuid(uuid);
            if (!data) {
                setError('Session not found locally. Run Sync if it was created on another device.');
            } else {
                setSession(data);
            }
        } catch (e) {
            setError(e?.message || 'Failed to read local session');
        } finally {
            setLoading(false);
        }
    }, [uuid]);

    useEffect(() => {
        reload();
        // Открыли сессию — пробуем досинкать офлайн-очередь и перечитываем при успехе.
        processOutbox().then((n) => { if (n > 0) reload(); }).catch(() => {});
    }, [reload]);

    // Курс с презентацией — из IndexedDB (по внутреннему id, fallback по externalId для старых записей).
    useEffect(() => {
        if (!session) { setCourse(null); return; }
        let cancelled = false;
        getLocalCourses().then(courses => {
            if (cancelled) return;
            const found = (courses || []).find(c =>
                (session.courseId && c.id === session.courseId) ||
                (session.courseExternalId && c.externalId === session.courseExternalId)
            ) || null;
            setCourse(found);
        }).catch(() => setCourse(null));
        return () => { cancelled = true; };
    }, [session]);

    const hasNativePresentation = ['en', 'es'].some(
        l => course?.presentation?.[l]?.source === 'local' && course.presentation[l].slides?.length
    );

    // Все участники прошли квиз (completed) И хотя бы один отправлен →
    // показываем зелёную кнопку "Finish training" вместо набора Add/Send/Close.
    const participants = session?.participantsList || [];
    const allCompleted = participants.length > 0 &&
        participants.every(p => !!p.completedAt);
    const pendingSync = !!session?._pendingSync || participants.some(p => p._pendingSync);

    // Auto-poll: тянем актуальное состояние с сервера и обновляем IDB.
    // Опт: 20 секунд + только когда tab активный + остановка если сессия closed.
    useEffect(() => {
        if (!uuid) return;
        if (session?.status === 'closed') return;

        let cancelled = false;
        let timeoutId = null;

        const tick = async () => {
            if (cancelled) return;
            const sinceMutation = Date.now() - lastMutationAtRef.current;
            // Не делаем polling если мутация в процессе, или закончилась < 3s назад,
            // или таб ушёл в фон.
            if (
                document.visibilityState === 'visible' &&
                !mutatingRef.current &&
                sinceMutation > 3000
            ) {
                const fresh = await refreshSessionFromServer(uuid);
                // Перед сохранением ещё раз проверяем что мутация не началась за это время.
                if (!cancelled && fresh && !mutatingRef.current) {
                    setSession(fresh);
                }
            }
            if (!cancelled) {
                timeoutId = setTimeout(tick, 20000);
            }
        };

        // Pause/resume на смене видимости — реакция мгновенная.
        const onVisibility = () => {
            if (document.visibilityState === 'visible' && !timeoutId) {
                tick();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);

        timeoutId = setTimeout(tick, 20000);

        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [uuid, session?.status]);

    const withMutationLock = async (fn) => {
        mutatingRef.current = true;
        try {
            await fn();
        } finally {
            mutatingRef.current = false;
            lastMutationAtRef.current = Date.now();
        }
    };

    const handleSend = async () => {
        if (!session) return;
        if (!session.participantsList || session.participantsList.length === 0) {
            window.alert('Add participants first');
            return;
        }
        if (!window.confirm('Send SMS to all pending participants?')) return;
        setLoading(true);
        try {
            await withMutationLock(async () => {
                const resp = await sendSession(uuid);
                await reload();
                window.alert(`Sent: ${resp?.stats?.sent ?? 0}\nFailed: ${resp?.stats?.failed ?? 0}`);
            });
        } catch (e) {
            window.alert('Send failed: ' + (e?.response?.data?.error || e.message));
        } finally {
            setLoading(false);
        }
    };

    const handleReset = async (p) => {
        if (!window.confirm(`Reset ${p.displayName}'s attempt?\nThey will be able to take the quiz again at the same URL.`)) return;
        await withMutationLock(async () => {
            await resetParticipant(uuid, p.id);
            await reload();
        });
    };

    // Ручной («очный») проход — без квиза, работает офлайн (ставится в очередь синка).
    const handleMarkPassed = async (p) => {
        if (!window.confirm(`Mark ${p.displayName} as passed?\nIn-person completion (no quiz). A certificate will be generated once synced.`)) return;
        await withMutationLock(async () => {
            await markPassed(uuid, p);
            await reload();
        });
    };

    const handleRemove = async (p) => {
        const warning = p.smsStatus === 'sent'
            ? '\nTheir SMS link will stop working.'
            : '';
        if (!window.confirm(`Remove ${p.displayName} from this session?${warning}`)) return;
        await withMutationLock(async () => {
            try {
                await removeParticipant(uuid, p.id);
            } catch (e) {
                window.alert('Remove failed: ' + (e?.response?.data?.error || e.message));
            }
            await reload();
        });
    };

    // Открытие редактора номера: сразу подтягиваем историю правок (кто/на что/когда).
    const openPhoneEditor = async (row) => {
        setPhoneHistory([]);
        setPhoneEditFor(row);
        if (typeof row.id === 'number') {
            const h = await fetchParticipantPhoneHistory(uuid, row.id);
            setPhoneHistory(h);
        }
    };

    // Смена номера прямо из листа: сохраняется и в карточку человека, и во внешнюю БД.
    const handlePhoneSave = async (digits) => {
        const p = phoneEditFor;
        setPhoneEditFor(null);
        if (!p) return;
        await withMutationLock(async () => {
            try {
                await updateParticipantPhone(uuid, p, digits);
            } catch (e) {
                window.alert('Could not update phone: ' + (e?.response?.data?.error || e.message));
            }
            await reload();
        });
    };

    const handleClose = async () => {
        const msg = allCompleted
            ? 'Finish this training? All participants completed the quiz — their certificates stay available in Symfony admin. Session will disappear from this list.'
            : 'Close this session? Links will stop working and it will disappear from this list (history stays in Symfony admin).';
        if (!window.confirm(msg)) return;
        await closeSession(uuid);
        navigate('/training-sessions');
    };

    const openResult = (p) => {
        setResultParticipant(p);
        setResultOpen(true);
    };

    const columns = [
        {
            field: 'displayName', headerName: 'Name', flex: 1, minWidth: 130,
            renderCell: ({ row }) => (
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight={500} noWrap component="span">
                        {row.displayName}
                    </Typography>
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                        ({row.kind === 'temp' ? 'T' : 'E'})
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'phone', headerName: 'Phone', width: 165, sortable: false,
            valueGetter: (value, row) => row?.phoneFormatted || row?.phoneRaw,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography variant="body2" noWrap component="span">
                        {row.phoneFormatted || row.phoneRaw || '—'}
                    </Typography>
                    <Tooltip title="Change phone number">
                        <IconButton size="small" sx={{ ml: 'auto', color: '#6b7280' }}
                            onClick={(e) => { e.stopPropagation(); openPhoneEditor(row); }}>
                            <EditIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
        {
            field: 'state', headerName: 'Status', width: 150, sortable: false,
            renderCell: ({ row }) => <StateBadge row={row} />,
        },
        {
            field: 'actions', headerName: 'Actions', width: 230, sortable: false, filterable: false, align: 'right', headerAlign: 'right',
            renderCell: ({ row }) => {
                const isCompleted = !!row.completedAt;
                const isManual = row.completionType === 'manual';
                // Удалять можно и после отправки SMS — ссылка просто перестанет работать.
                // Защищены только те, кто уже прошёл тренинг (есть сертификат/внешняя запись).
                const canRemove = !isCompleted;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', gap: 0.5 }}>
                        {/* Нет сети → SMS не отправить; вместо галочки — явная подписанная кнопка «Mark passed». */}
                        {isOffline && !isCompleted && (
                            <Button size="small" variant="contained" startIcon={<CheckCircleIcon />}
                                onClick={(e) => { e.stopPropagation(); handleMarkPassed(row); }}
                                sx={{ backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' }, textTransform: 'none', minWidth: 0, px: 1.25, whiteSpace: 'nowrap' }}>
                                Mark passed
                            </Button>
                        )}
                        {!isOffline && isCompleted && !isManual && (
                            <Tooltip title="View answers">
                                <IconButton sx={{ width: 44, height: 44, color: '#2196f3' }}
                                    onClick={(e) => { e.stopPropagation(); openResult(row); }}>
                                    <VisibilityIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        {!isOffline && isCompleted && !isManual && (
                            <Tooltip title="Reset — let them take the quiz again">
                                <IconButton sx={{ width: 44, height: 44, color: '#ff9800' }}
                                    onClick={(e) => { e.stopPropagation(); handleReset(row); }}>
                                    <ReplayIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        {canRemove && (
                            <Tooltip title="Remove from session">
                                <IconButton sx={{ width: 44, height: 44, color: '#f44336' }}
                                    onClick={(e) => { e.stopPropagation(); handleRemove(row); }}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                );
            },
        },
    ];

    return (
        <Box sx={{
            position: 'fixed', top: '64px', left: 0, right: 0, bottom: 0,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: '#f5f5f5',
        }}>
            <Box sx={{
                backgroundColor: 'white',
                borderBottom: '1px solid #e0e0e0',
                p: 2,
                flex: '0 0 auto',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: '1 1 auto' }}>
                    <IconButton onClick={() => navigate('/training-sessions')} edge="start">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={600} noWrap>{session?.courseName || 'Training Session'}</Typography>
                        {session && (
                            <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.25 }}>
                                <StatusChip status={session.status} />
                                {isOffline && (
                                    <Chip icon={<CloudOffIcon sx={{ fontSize: 16 }} />} label="Offline — mark passed manually" size="small"
                                        sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: '#e5e7eb', color: '#374151' }} />
                                )}
                                {pendingSync && (
                                    <Chip label="Not synced" size="small"
                                        sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: '#fff3cd', color: '#7a5b00' }} />
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    {session.participantsList?.length || 0} participant{(session.participantsList?.length || 0) === 1 ? '' : 's'}
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                </Box>
                <Stack direction="row" spacing={1}>
                    {hasNativePresentation && (
                        <Button variant="outlined" startIcon={<SlideshowIcon />} onClick={() => setPreviewOpen(true)}>
                            Present
                        </Button>
                    )}
                    {!allCompleted && (
                        <>
                            <Button variant="outlined" startIcon={<AddIcon />}
                                onClick={() => { setAddSource('people'); setAddKey(k => k + 1); setAddOpen(true); }}
                                disabled={session?.status === 'closed'}>
                                Add
                            </Button>
                            <Button variant="outlined" startIcon={<GroupAddIcon />}
                                onClick={() => { setAddSource('roster'); setAddKey(k => k + 1); setAddOpen(true); }}
                                disabled={session?.status === 'closed'}
                                title="Add everyone from this week's roster">
                                From Roster
                            </Button>
                            <Button variant="contained" startIcon={<SendIcon />} onClick={handleSend}
                                disabled={session?.status === 'closed' || loading || isOffline}
                                title={isOffline ? 'No internet — SMS links can’t be sent. Mark participants as passed instead.' : ''}
                                sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}>
                                Send
                            </Button>
                            <Button variant="outlined" color="warning" onClick={handleClose}
                                disabled={session?.status === 'closed'}>
                                Close
                            </Button>
                        </>
                    )}
                    {allCompleted && (
                        <Button variant="contained" startIcon={<DoneAllIcon />} onClick={handleClose}
                            disabled={session?.status === 'closed'}
                            sx={{ backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}>
                            Finish training
                        </Button>
                    )}
                </Stack>
            </Box>

            {loading && <LinearProgress />}
            {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

            <Box sx={{ flex: '1 1 auto', overflow: 'hidden', backgroundColor: 'white' }}>
                <DataGrid
                    rows={session?.participantsList || []}
                    columns={columns}
                    loading={loading}
                    getRowId={row => row.id ?? row.clientId}
                    rowHeight={56}
                    columnHeaderHeight={44}
                    disableSelectionOnClick
                    disableColumnMenu
                    hideFooter={(session?.participantsList?.length || 0) <= 25}
                    initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
                    pageSizeOptions={[25, 50, 100]}
                    sx={mergeDataGridStyles({
                        border: 'none',
                        '& .MuiDataGrid-cell': {
                            fontSize: '0.9rem',
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: '14px',
                            paddingRight: '14px',
                            borderBottom: '1px solid #f0f0f0',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            paddingLeft: '14px',
                            paddingRight: '14px',
                            backgroundColor: '#fafafa',
                        },
                        '& .MuiDataGrid-columnHeader[data-field="actions"], & .MuiDataGrid-cell[data-field="actions"]': {
                            paddingRight: '10px',
                        },
                    })}
                    localeText={{ noRowsLabel: 'No participants yet. Tap "Add" to invite people.' }}
                />
            </Box>

            <AddParticipantModal
                key={addKey}
                open={addOpen}
                source={addSource}
                onClose={() => setAddOpen(false)}
                sessionUuid={uuid}
                alreadySelected={participants.map(p => ({
                    FullName: p.displayName,
                    _type: p.kind === 'temp' ? 'temp' : 'employee',
                }))}
                onAdded={() => { setAddOpen(false); reload(); }}
            />

            {phoneEditFor && (
                <TempPhoneModal
                    open={!!phoneEditFor}
                    onClose={() => { setPhoneEditFor(null); setPhoneHistory([]); }}
                    onConfirm={handlePhoneSave}
                    participantName={phoneEditFor.displayName}
                    initialPhone={phoneEditFor.phoneRaw || ''}
                    kind={phoneEditFor.kind === 'temp' ? 'temp' : 'employee'}
                    history={phoneHistory}
                />
            )}

            {resultParticipant && (
                <ParticipantResultModal
                    open={resultOpen}
                    onClose={() => setResultOpen(false)}
                    sessionUuid={uuid}
                    participant={resultParticipant}
                />
            )}

            <SlideShowModal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                course={course}
                defaultLanguage={session?.defaultLanguage || 'en'}
            />
        </Box>
    );
}

function StatusChip({ status, sx }) {
    const map = {
        draft: { label: 'Draft', color: 'default' },
        active: { label: 'Active', color: 'success' },
        closed: { label: 'Closed', color: 'warning' },
    };
    const cfg = map[status] || { label: status || '—', color: 'default' };
    return <Chip label={cfg.label} color={cfg.color} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', ...sx }} />;
}

function StateBadge({ row }) {
    const state = row.state;
    const map = {
        pending:     { label: 'Not sent yet',    color: 'default' },
        sent:        { label: 'SMS sent',        color: 'info' },
        failed:      { label: 'SMS failed',      color: 'error' },
        in_progress: { label: 'Filling now…',    color: 'warning' },
        completed:   { label: 'Completed',       color: 'success' },
        expired:     { label: 'Expired',         color: 'error' },
    };
    const cfg = map[state] || { label: state || '—', color: 'default' };
    const score = row.score ? `${row.score.correct}/${row.score.total}` : null;
    const manual = row.completionType === 'manual';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%', flexWrap: 'wrap' }}>
            <Chip label={cfg.label} color={cfg.color} size="small"
                sx={{ fontWeight: 600, fontSize: '0.72rem', height: 22 }}
                title={row.smsError || ''} />
            {score && (
                <Typography variant="caption" fontWeight={700}
                    color={row.score.correct === row.score.total ? 'success.main' : 'warning.main'}>
                    {score}
                </Typography>
            )}
            {manual && (
                <Typography variant="caption" color="text.secondary">in&nbsp;person</Typography>
            )}
            {/* Сервер не принял изменение — показываем что именно и почему,
                вместо тихого отката (добавление / отметка «прошёл» / смена номера). */}
            {row._failed && (
                <Chip label={row._failedLabel || 'Sync failed'} size="small" title={row._error || 'Sync failed'}
                    sx={{ fontWeight: 600, fontSize: '0.68rem', height: 22, bgcolor: '#fee2e2', color: '#991b1b' }} />
            )}
            {row._pendingSync && !row._failed && (
                <Typography variant="caption" sx={{ color: '#b45309' }}>· not&nbsp;synced</Typography>
            )}
        </Box>
    );
}
